// eslint-disable-next-line no-restricted-imports
import type { ModelType } from '@sb/robot';
import { convertDegreesToRadians, isApproximatelyEqual } from '@sb/utilities';

import type { ArmJointPositions } from './ArmJointPositions';

export const RO2_JOINT_ANGLES_FOAM_CASE: ArmJointPositions = [
  -0, -105, 163, -140, 180, 186,
].map((degrees) => convertDegreesToRadians(degrees)) as ArmJointPositions;

export const RO2_UNBOX_TARGET_JOINT_ANGLES = [0, -65, 130, -65, 0, 0].map(
  convertDegreesToRadians,
) as ArmJointPositions;

export const RO2_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES = [
  0, -105, 160, -120, 180, 186,
].map(convertDegreesToRadians) as ArmJointPositions;

export const THOR_JOINT_ANGLES_BOX: ArmJointPositions = [
  0, 0, 180, 180, 0, 0,
].map((degrees) => convertDegreesToRadians(degrees)) as ArmJointPositions;

export const THOR_UNBOX_TARGET_JOINT_ANGLES = [0, -45, 105, 60, 0, 0].map(
  convertDegreesToRadians,
) as ArmJointPositions;

export const THOR_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES = [
  0, 0, 160, 180, 0, 0,
].map(convertDegreesToRadians) as ArmJointPositions;

export const SPARK_JOINT_ANGLES_BOX: ArmJointPositions = THOR_JOINT_ANGLES_BOX;
export const SPARK_UNBOX_TARGET_JOINT_ANGLES: ArmJointPositions =
  THOR_UNBOX_TARGET_JOINT_ANGLES;
export const SPARK_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES: ArmJointPositions =
  THOR_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES;

// Model-specific boxing/unboxing angles
const BOXING_JOINT_ANGLES_BY_MODEL: Record<ModelType, ArmJointPositions> = {
  RO1: RO2_JOINT_ANGLES_FOAM_CASE,
  Thor: THOR_JOINT_ANGLES_BOX,
  Spark: SPARK_JOINT_ANGLES_BOX,
};

const UNBOX_TARGET_JOINT_ANGLES_BY_MODEL: Record<ModelType, ArmJointPositions> =
  {
    RO1: RO2_UNBOX_TARGET_JOINT_ANGLES,
    Thor: THOR_UNBOX_TARGET_JOINT_ANGLES,
    Spark: SPARK_UNBOX_TARGET_JOINT_ANGLES,
  };

const INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES_BY_MODEL: Record<
  ModelType,
  ArmJointPositions
> = {
  RO1: RO2_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES,
  Thor: THOR_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES,
  Spark: SPARK_INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES,
};

export function getBoxingJointAngles(modelType: ModelType): ArmJointPositions {
  return BOXING_JOINT_ANGLES_BY_MODEL[modelType];
}

export function getUnboxingAngles(modelType: ModelType): {
  unbox: ArmJointPositions;
  intermediate: ArmJointPositions;
} {
  return {
    unbox: UNBOX_TARGET_JOINT_ANGLES_BY_MODEL[modelType],
    intermediate: INTERMEDIATE_UNBOX_TARGET_JOINT_ANGLES_BY_MODEL[modelType],
  };
}

/**
 * Collision link pairs to ignore during boxing/unboxing moves.
 * These pairs are allowed to collide without triggering a failure.
 */
type CollisionLinkPair = [string, string];

// Common collision pairs shared by all models (end effector and wrist area)
const COMMON_BOXING_IGNORED_COLLISION_PAIRS: CollisionLinkPair[] = [
  // End effector link, even when no EE there is a default 3FG15 collision box
  ['eef_collision', '*'],
  ['wrist_3_cable_link', '*'],
  // Dual Quick Changer link
  ['connector_collision', '*'],
  ['gripper1_collision', '*'],
  ['gripper2_collision', '*'],
];

// Thor/Spark specific: forearm_link collides with shoulder_link at J2=180° (boxing position)
// due to 5% collision mesh padding in URDF. This is not a physical collision.
const THOR_BOXING_IGNORED_COLLISION_PAIRS: CollisionLinkPair[] = [
  ...COMMON_BOXING_IGNORED_COLLISION_PAIRS,
  ['forearm_link', 'shoulder_link'],
];

const BOXING_IGNORED_COLLISION_PAIRS_BY_MODEL: Record<
  ModelType,
  CollisionLinkPair[]
> = {
  RO1: COMMON_BOXING_IGNORED_COLLISION_PAIRS,
  Thor: THOR_BOXING_IGNORED_COLLISION_PAIRS,
  Spark: THOR_BOXING_IGNORED_COLLISION_PAIRS,
};

/**
 * Get the collision link pairs to ignore during boxing/unboxing for a given model type.
 */
export function getBoxingIgnoredCollisionPairs(
  modelType: ModelType,
): CollisionLinkPair[] {
  return BOXING_IGNORED_COLLISION_PAIRS_BY_MODEL[modelType];
}

/**
 * Per-joint angular proximity thresholds (degrees) defining "close to boxing position".
 * When all joints are within their respective threshold of the boxing angles,
 * FK-based collision checks are suppressed to avoid false positives from URDF
 * mesh overlap at packed joint configurations.
 *
 * Each element corresponds to joints J1–J6.
 */
const RO1_BOXING_PROXIMITY_DEGREES: [
  number,
  number,
  number,
  number,
  number,
  number,
] = [2, 2, 3, 3, 2, 2];

const THOR_BOXING_PROXIMITY_DEGREES: [
  number,
  number,
  number,
  number,
  number,
  number,
] = [2, 2, 3, 3, 2, 2];

const SPARK_BOXING_PROXIMITY_DEGREES: [
  number,
  number,
  number,
  number,
  number,
  number,
] = [2, 2, 3, 3, 2, 2];

const BOXING_PROXIMITY_THRESHOLDS_BY_MODEL: Record<
  ModelType,
  ArmJointPositions
> = {
  RO1: RO1_BOXING_PROXIMITY_DEGREES.map(
    convertDegreesToRadians,
  ) as ArmJointPositions,
  Thor: THOR_BOXING_PROXIMITY_DEGREES.map(
    convertDegreesToRadians,
  ) as ArmJointPositions,
  Spark: SPARK_BOXING_PROXIMITY_DEGREES.map(
    convertDegreesToRadians,
  ) as ArmJointPositions,
};

export function getBoxingProximityThresholds(
  modelType: ModelType,
): ArmJointPositions {
  return BOXING_PROXIMITY_THRESHOLDS_BY_MODEL[modelType];
}

/**
 * Returns true when all joint angles are within the per-joint proximity thresholds
 * of the boxing position for the given model type.
 */
export function isNearBoxingPosition(
  jointAngles: ArmJointPositions,
  modelType: ModelType,
): boolean {
  const boxingAngles = getBoxingJointAngles(modelType);
  const thresholds = getBoxingProximityThresholds(modelType);

  return jointAngles.every((angle, index) =>
    isApproximatelyEqual(angle, boxingAngles[index], thresholds[index]),
  );
}
